import { baseApiUrl, thoughtSpotUrl } from 'src/config';
import { getRearchiveUrl } from 'src/utils/rearchiveUrl';
import { CLIENT_IP_HEADER, getClientIpHeader, peekClientIpHeader } from './getClientIp';

const originOf = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

/**
 * Whether a request is bound for one of our own backends. Only these get the client-IP header:
 * on any other origin (ipify itself, Keycloak, Datadog, Split, FullStory, Intercom) an unknown
 * custom header fails the CORS preflight and with it the request.
 *
 * Rearchive's origin depends on the realm, so it is resolved per request rather than once.
 */
export const isOwnBackend = (url: string): boolean => {
  const origin = originOf(url);
  if (!origin) return false;
  return [baseApiUrl, thoughtSpotUrl, getRearchiveUrl()].some(
    (backend) => originOf(backend) === origin
  );
};

const urlOf = (input: RequestInfo | URL): string =>
  input instanceof Request ? input.url : String(input);

let installed = false;

/**
 * Wraps `window.fetch` so every request to our own backends carries `X-Client-IP`, without each
 * call site having to add it. Axios doesn't go through fetch (it uses XHR in the browser), so the
 * shared instance in `src/services/interceptor.tsx` and the automation calls still add the header
 * themselves.
 *
 * Headers the caller sets are kept; the IP header is only added, and is omitted when the IP is
 * unknown. A `keepalive` request (the unload autosave) must reach the network before the page
 * goes, so it reads the cached IP only and never waits on a lookup.
 */
export const installClientIpFetch = () => {
  if (installed) return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);

  const withIpHeader = (
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    ipHeader: Record<string, string>
  ) => {
    const ip = ipHeader[CLIENT_IP_HEADER];
    if (!ip) return nativeFetch(input, init);
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    headers.set(CLIENT_IP_HEADER, ip);
    return nativeFetch(input, { ...init, headers });
  };

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isOwnBackend(urlOf(input))) return nativeFetch(input, init);

    //  Synchronous on purpose: an `await` here would defer the keepalive request past unload.
    const keepalive = init?.keepalive ?? (input instanceof Request && input.keepalive);
    if (keepalive) return withIpHeader(input, init, peekClientIpHeader());

    return getClientIpHeader().then((ipHeader) => withIpHeader(input, init, ipHeader));
  };
};

/** Test-only: lets a test install the wrapper again over a fresh `window.fetch`. */
export const resetClientIpFetchInstall = () => {
  installed = false;
};
